import React, { useState } from 'react'
import { Button, Chip } from "@material-ui/core"
import { Col, Row } from "react-bootstrap"
import strings from 'strings'
import { joinAttributes } from 'tools/formatters'
import StatusIndicator from 'components/shared/StatusIndicator/index'
import Moment from "react-moment"
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import AuthService from "../../../../tools/AuthService";
import CreditCost from 'components/shared/CreditCost'

const sosStrings = strings.APPLICANT.SOS

const getValueFromData = (data, field) => {
    if (data !== null && data !== undefined) {
        if (field === 'categories' && Array.isArray(data[field])) return data[field].join(', ')
        if (data[field] != null && data[field] !== undefined){
            return data[field]
        }else{
            return '-'
        }
    }
    return '-'
}

const AllStatesSearchDetailsCard = (props) => {
    const { searchData, viewResult, searchAlternative, searchLoading, creditCosts } = props
    const { creditUsageEnabled } = AuthService.getClientFeatures()
    const [showAllResults, setShowAllResults] = useState(false)
    const [showBestMatch, setShowBestMatch] = useState(true)

    if (!searchData) return null

    const { results = [], bestMatch, status, completedStates, businessName, createdAt } = searchData

    // Show only first 5 results initially, all if expanded
    const displayedResults = showAllResults ? results : results.slice(0, 5)
    const hasMoreResults = results.length > 5

    const getStatusColor = (status) => {
        if (!status) return 'gray'
        const normalizedStatus = status.toLowerCase()
        // Check for exact active status, not inactive
        return (normalizedStatus === 'active' || normalizedStatus === 'active/compliance') ? 'green' : 'gray'
    }

    const getConfidenceBadgeColor = (confidence) => {
        const conf = parseFloat(confidence)
        if (conf >= 0.8) return 'success'
        if (conf >= 0.6) return 'warning'
        return 'default'
    }

    const getEndIcon = () => {
        if (creditCosts){
            const costObj = creditCosts.find(cost => cost.requestType === 'SOS_SEARCH');
            return costObj ? costObj.creditCost : 0;
        }
        return 0;
    }

    const renderSearchSummary = () => (
        <div className="mb-4">
            <Row>
                <Col md={3}>
                    <p className='fieldTitle mb-0'>Business Name</p>
                    <p className='fieldValue bold-font'>{businessName}</p>
                </Col>
                <Col md={2}>
                    <p className='fieldTitle mb-0'>Status</p>
                    <StatusIndicator
                        color={status === 'COMPLETED' ? 'green' : 'orange'}
                        text={status}
                        side='left'
                    />
                </Col>
                <Col md={2}>
                    <p className='fieldTitle mb-0'>States Completed</p>
                    <p className='fieldValue'>{completedStates}</p>
                </Col>
                <Col md={2}>
                    <p className='fieldTitle mb-0'>Total Results</p>
                    <p className='fieldValue'>{results.length}</p>
                </Col>
                <Col md={3}>
                    <p className='fieldTitle mb-0'>Search Date</p>
                    <Moment className='fieldValue' format="MM/DD/YYYY HH:mm">
                        {createdAt}
                    </Moment>
                </Col>
            </Row>
        </div>
    )

    const renderBestMatch = () => {
        if (!bestMatch?.results) return null

        const result = bestMatch.results
        const address = joinAttributes({
            street: result.physicalAddressStreet,
            city: result.physicalAddressCity,
            state: result.physicalAddressState,
            zip: result.physicalAddressZip
        })

        return (
            <div className="cardStyle mb-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="mb-0">Best Match Result</h6>
                    <Button
                        size="small"
                        onClick={() => setShowBestMatch(!showBestMatch)}
                    >
                        {showBestMatch ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </Button>
                </div>

                {showBestMatch && (
                    <>
                        <div className='d-flex justify-content-between mb-3'>
                            <div className='flex-fill'>
                                <div className='d-flex align-items-center mb-2'>
                                    <p className='bold-font mb-0 mr-3'>{result.title}</p>
                                    <StatusIndicator
                                        color={getStatusColor(result.normalizedStatus)}
                                        text={result.normalizedStatus}
                                        side='left'
                                    />
                                    <Chip
                                        label={`${(result.confidenceLevel * 100).toFixed(0)}% Confidence`}
                                        color="primary"
                                        size="small"
                                        className="ml-2"
                                    />
                                </div>
                                <p className='mb-0'>{address}</p>
                                <p className='mb-0 text-muted'>State: {bestMatch.state}</p>
                            </div>
                            <div className='d-flex justify-content-end align-items-center'>
                                <div className='greenLabel mr-2'>{sosStrings.BEST_MATCH}</div>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    className='mr-2'
                                    onClick={() => viewResult(result, searchData.searchGuid)}
                                >
                                    {sosStrings.VIEW_MORE}
                                </Button>
                            </div>
                        </div>

                        <Row className='pt-3'>
                            <Col md={2}>
                                <p className='fieldTitle mb-0'>Entity Type</p>
                                <p className='fieldValue'>{getValueFromData(result, 'entityType')}</p>
                            </Col>
                            <Col md={2}>
                                <p className='fieldTitle mb-0'>Filing Date</p>
                                <p className='fieldValue'>{getValueFromData(result, 'filingDate')}</p>
                            </Col>
                            <Col md={2}>
                                <p className='fieldTitle mb-0'>State of Formation</p>
                                <p className='fieldValue'>{getValueFromData(result, 'stateOfFormation')}</p>
                            </Col>
                            <Col md={2}>
                                <p className='fieldTitle mb-0'>Agent Name</p>
                                <p className='fieldValue'>{getValueFromData(result, 'agentName')}</p>
                            </Col>
                        </Row>
                    </>
                )}
            </div>
        )
    }

    const renderAllResults = () => (
        <div className="cardStyle">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">All Search Results ({results.length})</h6>
                {hasMoreResults && (
                    <Button
                        size="small"
                        onClick={() => setShowAllResults(!showAllResults)}
                    >
                        {showAllResults ? 'Show Less' : 'Show All'}
                        {showAllResults ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </Button>
                )}
            </div>

            {displayedResults.map((result, index) => {
                const address = joinAttributes({
                    street: result.physicalAddressStreet,
                    city: result.physicalAddressCity,
                    state: result.physicalAddressState,
                    zip: result.physicalAddressZip
                })

                return (
                    <div key={result.sosId || index} className="mb-3">
                        {index > 0 && <hr />}
                        <div className='d-flex justify-content-between'>
                            <div className='flex-fill'>
                                <div className='d-flex align-items-center mb-1'>
                                    <p className='possibleAlternativeTitle mb-0 mr-3'>{result.businessName}</p>
                                    <StatusIndicator
                                        color={getStatusColor(result.status)}
                                        text={result.status}
                                        side='left'
                                    />
                                    <Chip
                                        label={`${(parseFloat(result.confidenceLevel) * 100).toFixed(0)}% Confidence`}
                                        color={getConfidenceBadgeColor(result.confidenceLevel)}
                                        size="small"
                                        className="ml-2"
                                    />
                                </div>
                                <p className='mb-0 text-muted'>SOS ID: {result.sosId}</p>
                                <p className='mb-0 text-muted'>State: {result.stateCode}</p>
                                {address && <p className='mb-0'>{address}</p>}
                            </div>
                            <div className='d-flex align-items-center'>
                                <div className='yellowLabel mr-2'>{sosStrings.ALTERNATIVE}</div>
                                <Button
                                    variant="outlined"
                                    color='primary'
                                    size="small"
                                    onClick={() => searchAlternative(result.sosId, result.businessName)}
                                    disabled={searchLoading || !result.sosId}
                                    endIcon={creditUsageEnabled && creditCosts ?
                                        <CreditCost cost={getEndIcon()} /> : undefined}
                                >
                                    {sosStrings.RESEARCH}
                                </Button>
                            </div>
                        </div>
                    </div>
                )
            })}

            {hasMoreResults && !showAllResults && (
                <div className="text-center mt-3">
                    <Button
                        color="primary"
                        onClick={() => setShowAllResults(true)}
                    >
                        Show {results.length - 5} More Results
                    </Button>
                </div>
            )}
        </div>
    )

    return (
        <div style={{padding: "2rem"}}>
            {renderSearchSummary()}
            {renderBestMatch()}
            {results.length > 0 && renderAllResults()}
        </div>
    )
}

export default AllStatesSearchDetailsCard